'use client';

//
// BusinessStoryCardsRenderer — the 9:16 vertical "stories" player.
//
// Faithful port of the legacy story-cards player into the micro popup
// renderer contract. Every player feature is preserved: progress
// segments, auto-advance timers, tap-zone + hold-to-pause + keyboard
// navigation, mute, layered cards (text / pill / metric / quote / list
// / html / image / video / button), entrance animations, CTA actions,
// and the post-completion takeaways review.
//
// Adaptations for the new popup contract:
//   - On completion the renderer fires onComplete() exactly once; the
//     popup marks MicroSource.progress completed. No localStorage —
//     the server row is the source of truth.
//   - The legacy header bookmark toggle is dropped: the new
//     RendererProps carries no favorite wiring (none of the ported
//     renderers surface favorites).
//   - The immersive player renders inside the popup's modal; the modal
//     goes transparent for this kind (see MicroContentPopup storyMode)
//     so the in-phone close is the only close.
//   - Scoped player CSS is injected once into <head>.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { RendererProps } from '../types';
import { STORY_CARDS_CSS } from './businessStoryCards/storyCardsStyles';
import type {
  StoryCssVars,
  StoryLayerAnimation,
  StoryLayerStyle,
} from './businessStoryCards/model';
import {
  buildStoryProgress,
  getCardBackgroundSource,
  getCardDurationMs,
  getCurrentStoryCard,
  getLayerMediaSource,
  nextStoryCard,
  normalizeStoryCardsConfig,
  previousStoryCard,
  recordStoryAction,
  resetStoryRun,
  startStoryRun,
  toggleStoryMute,
  toggleStoryPause,
  type NormalizedStoryConfig,
  type StoryCard,
  type StoryConfig,
  type StoryLayer as StoryLayerT,
  type StoryProgress,
} from './businessStoryCards/logic';

// One-time stylesheet injection (idempotent across opens / instances).
function ensureStoryStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('bsc-story-styles')) return;
  const el = document.createElement('style');
  el.id = 'bsc-story-styles';
  el.textContent = STORY_CARDS_CSS;
  document.head.appendChild(el);
}

export function BusinessStoryCardsRenderer({ item, onComplete, onClose }: RendererProps) {
  const { t } = useTranslation('site-builder-blocks');
  const config = useMemo<NormalizedStoryConfig>(
    () => normalizeStoryCardsConfig((item.body ?? {}) as StoryConfig),
    [item.body],
  );
  const [progress, setProgress] = useState<StoryProgress>(() => buildStoryProgress(config));
  const [elapsedMs, setElapsedMs] = useState(0);
  const autoAdvanceLockRef = useRef(false);
  const completedRef = useRef(false);
  const startedRef = useRef(false);

  const card = getCurrentStoryCard(config, progress);

  useEffect(() => {
    ensureStoryStyles();
  }, []);

  // Auto-start once on mount (the intro state immediately enters play —
  // the popup is itself the "tap to open" affordance).
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    setProgress((current: StoryProgress) => startStoryRun(config, current));
  }, [config]);

  // Completion → report up to the popup exactly once.
  useEffect(() => {
    if (progress.status === 'completed' && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  }, [progress.status, onComplete]);

  function next(source = 'manual') {
    setElapsedMs(0);
    setProgress((current: StoryProgress) => nextStoryCard(config, current, source));
  }
  function previous() {
    setElapsedMs(0);
    setProgress((current: StoryProgress) => previousStoryCard(config, current));
  }
  function pauseToggle(forceValue?: boolean) {
    setProgress((current: StoryProgress) => toggleStoryPause(current, forceValue));
  }
  function muteToggle() {
    setProgress((current: StoryProgress) => toggleStoryMute(current));
  }
  function restart() {
    completedRef.current = false;
    setElapsedMs(0);
    const fresh = resetStoryRun(config);
    setProgress(startStoryRun(config, fresh));
  }
  function handleLayerAction(layer: StoryLayerT) {
    setProgress((current: StoryProgress) => recordStoryAction(config, current, card, layer));
  }

  // Reset the per-card timer + auto-advance lock whenever the card changes.
  useEffect(() => {
    autoAdvanceLockRef.current = false;
    setElapsedMs(0);
  }, [progress.currentIndex, progress.status]);

  // Auto-advance ticker (70ms cadence, matching the POC).
  useEffect(() => {
    if (progress.status !== 'playing' || progress.paused || !config.player.autoAdvance || !card)
      return undefined;
    const duration = getCardDurationMs(config, card);
    const step = 70;
    const timer = window.setInterval(() => {
      setElapsedMs((current) => {
        const nextElapsed = current + step;
        if (nextElapsed >= duration) {
          if (!autoAdvanceLockRef.current) {
            autoAdvanceLockRef.current = true;
            window.setTimeout(() => next('auto'), 0);
          }
          return duration;
        }
        return nextElapsed;
      });
    }, step);
    return () => window.clearInterval(timer);
  }, [progress.status, progress.paused, progress.currentIndex, config, card]);

  // Keyboard shortcuts (next / prev / pause / reset / mute / close).
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      const editing = target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
      if (editing) return;
      const shortcuts = config.player.keyboardShortcuts || {};
      if ((shortcuts.close || []).includes(event.key)) {
        event.preventDefault();
        onClose();
        return;
      }
      if ((shortcuts.reset || []).includes(event.key)) {
        event.preventDefault();
        restart();
        return;
      }
      if ((shortcuts.mute || []).includes(event.key)) {
        event.preventDefault();
        muteToggle();
        return;
      }
      if (progress.status !== 'playing') return;
      if ((shortcuts.pause || []).includes(event.key)) {
        event.preventDefault();
        pauseToggle();
        return;
      }
      if (config.player.allowManualNavigation && (shortcuts.previous || []).includes(event.key)) {
        event.preventDefault();
        previous();
        return;
      }
      if (config.player.allowManualNavigation && (shortcuts.next || []).includes(event.key)) {
        event.preventDefault();
        next('manual');
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [config, progress.status, progress.paused, progress.currentIndex, card]);

  const duration = card ? getCardDurationMs(config, card) : 1;
  const currentFill =
    progress.status === 'playing' && config.player.autoAdvance && !progress.paused
      ? Math.min(100, (elapsedMs / duration) * 100)
      : progress.status === 'completed'
        ? 100
        : 0;

  return (
    <div
      className="bsc-root"
      style={{
        padding: 0,
        background: 'transparent',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        className="storypro-player-shell"
        aria-label={t('microStoryCards.player.label', { title: item.title })}
      >
        <div className="storypro-phone" style={getCanvasStyle(config)}>
          {progress.status === 'completed' ? (
            <StoryCompletion config={config} onRestart={restart} onClose={onClose} />
          ) : (
            <>
              <StoryProgressSegments
                config={config}
                progress={progress}
                currentFill={currentFill}
              />
              {config.player.showHeader && (
                <StoryPlayerHeader
                  config={config}
                  progress={progress}
                  onClose={onClose}
                  onMute={muteToggle}
                />
              )}
              {card && (
                <StoryCardCanvas
                  config={config}
                  card={card}
                  muted={progress.muted}
                  onLayerAction={handleLayerAction}
                />
              )}
              {config.player.tapToNavigate && config.player.allowManualNavigation && (
                <>
                  <button
                    type="button"
                    className="storypro-tap-zone storypro-tap-zone--left"
                    onClick={previous}
                    aria-label={t('microStoryCards.previous')}
                  />
                  <button
                    type="button"
                    className="storypro-tap-zone storypro-tap-zone--right"
                    onClick={() => next('manual')}
                    aria-label={t('microStoryCards.next')}
                  />
                </>
              )}
              {config.player.holdToPause && (
                <button
                  type="button"
                  className="storypro-hold-zone"
                  aria-label={t('microStoryCards.holdToPause')}
                  onPointerDown={() => pauseToggle(true)}
                  onPointerUp={() => pauseToggle(false)}
                  onPointerCancel={() => pauseToggle(false)}
                  onPointerLeave={() => pauseToggle(false)}
                />
              )}
              {progress.paused && (
                <div className="storypro-paused-badge">
                  <PauseIcon /> {t('microStoryCards.paused')}
                </div>
              )}
              <div className="storypro-bottom-hint">
                <TapIcon /> {t('microStoryCards.tapHint')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Progress segments
// -----------------------------------------------------------------------------

function StoryProgressSegments({
  config,
  progress,
  currentFill,
}: {
  config: NormalizedStoryConfig;
  progress: StoryProgress;
  currentFill: number;
}) {
  const { t } = useTranslation('site-builder-blocks');
  if (!config.player.showProgressBars) return null;
  return (
    <div className="storypro-progress" aria-label={t('microStoryCards.progress')}>
      {config.cards.map((item: StoryCard, index: number) => {
        const filled =
          index < progress.currentIndex ? 100 : index === progress.currentIndex ? currentFill : 0;
        return (
          <span key={item.id}>
            <i style={{ width: `${filled}%` }} />
          </span>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Header — avatar + group title + mute / close
// -----------------------------------------------------------------------------

function StoryPlayerHeader({
  config,
  progress,
  onClose,
  onMute,
}: {
  config: NormalizedStoryConfig;
  progress: StoryProgress;
  onClose: () => void;
  onMute: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const cover =
    config.story?.coverImageData || config.story?.coverImageUrl || config.company?.logoUrl || '';
  return (
    <header className="storypro-header">
      <div
        className="storypro-avatar"
        style={cover ? { backgroundImage: `url(${cssUrl(cover)})` } : undefined}
      >
        {!cover && (config.company?.avatarText || config.company?.name?.slice(0, 2) || 'S')}
      </div>
      <div className="storypro-header-copy">
        <strong>{config.story?.groupTitle || config.title}</strong>
        <span>
          {progress.currentIndex + 1} / {config.cards.length}
        </span>
      </div>
      {config.player.showMuteButton && (
        <button
          type="button"
          className="storypro-icon-button storypro-mute-button"
          onClick={onMute}
          aria-label={progress.muted ? t('microStoryCards.unmute') : t('microStoryCards.mute')}
          title={progress.muted ? t('microStoryCards.unmuteShort') : t('microStoryCards.muteShort')}
        >
          {progress.muted ? <VolumeOffIcon /> : <VolumeOnIcon />}
        </button>
      )}
      {config.player.showCloseButton && (
        <button
          type="button"
          className="storypro-icon-button storypro-close"
          onClick={onClose}
          aria-label={t('microStoryCards.close')}
          title={t('microStoryCards.closeShort')}
        >
          <CloseIcon />
        </button>
      )}
    </header>
  );
}

// -----------------------------------------------------------------------------
// Card canvas + layer engine
// -----------------------------------------------------------------------------

function StoryCardCanvas({
  config,
  card,
  muted,
  onLayerAction,
}: {
  config: NormalizedStoryConfig;
  card: StoryCard;
  muted: boolean;
  onLayerAction: (l: StoryLayerT) => void;
}) {
  const bg = card.background || {};
  const source = getCardBackgroundSource(card);
  const isVideoBg = bg.type === 'video' && source;
  const backgroundStyle = buildCardBackgroundStyle(card);
  return (
    <article
      className={`storypro-card storypro-template-${card.template || 'custom'}`}
      style={{ ...backgroundStyle, fontFamily: config.theme?.fontFamily || undefined }}
    >
      {isVideoBg && (
        <video
          className="storypro-bg-video"
          src={source}
          poster={bg.posterUrl || undefined}
          autoPlay
          playsInline
          loop
          muted={muted}
        />
      )}
      <div
        className="storypro-card-overlay"
        style={{
          background: bg.overlay || 'linear-gradient(180deg, rgba(0,0,0,.08), rgba(0,0,0,.72))',
        }}
      />
      <div className="storypro-layer-stage" aria-label={card.title}>
        {(card.layers || []).map((layer: StoryLayerT) => (
          <StoryLayer
            key={`${card.id}-${layer.id}-${progressiveAnimationKey(layer)}`}
            layer={layer}
            muted={muted}
            onAction={() => onLayerAction(layer)}
          />
        ))}
      </div>
      {config.player.showCaptions && card.description && (
        <span className="storypro-a11y-description">{card.description}</span>
      )}
    </article>
  );
}

function StoryLayer({
  layer,
  muted,
  onAction,
}: {
  layer: StoryLayerT;
  muted: boolean;
  onAction: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const type = layer.type || 'text';
  const src = getLayerMediaSource(layer);
  const layerStyle = getLayerStyle(layer);
  const animClass = `story-anim-${getLayerAnimationType(layer)}`;
  if (type === 'image') {
    return (
      <div className={`storypro-layer storypro-layer-media ${animClass}`} style={layerStyle}>
        {src ? (
          <img src={src} alt={layer.alt || layer.text || ''} />
        ) : (
          <span>{t('microStoryCards.layer.addImage')}</span>
        )}
      </div>
    );
  }
  if (type === 'video') {
    return (
      <div className={`storypro-layer storypro-layer-media ${animClass}`} style={layerStyle}>
        {src ? (
          <video
            src={src}
            poster={layer.posterUrl || undefined}
            autoPlay
            playsInline
            loop
            muted={muted}
          />
        ) : (
          <span>{t('microStoryCards.layer.addVideo')}</span>
        )}
      </div>
    );
  }
  if (type === 'button') {
    return (
      <button
        type="button"
        className={`storypro-layer storypro-layer-button ${animClass}`}
        style={layerStyle}
        onClick={(event) => {
          event.stopPropagation();
          onAction();
        }}
      >
        {layer.text || t('microStoryCards.layer.button')}
      </button>
    );
  }
  if (type === 'list') {
    return (
      <div className={`storypro-layer storypro-layer-list ${animClass}`} style={layerStyle}>
        {renderStoryListLayer(layer)}
      </div>
    );
  }
  if (type === 'html') {
    return (
      <div
        className={`storypro-layer storypro-layer-html ${animClass}`}
        style={layerStyle}
        dangerouslySetInnerHTML={{ __html: sanitizeStoryHtml(layer.html || layer.text || '') }}
      />
    );
  }
  return (
    <div className={`storypro-layer storypro-layer-${type} ${animClass}`} style={layerStyle}>
      {layer.text}
    </div>
  );
}

function getLayerAnimationType(layer: { animation?: StoryLayerAnimation } = {}) {
  return layer.animation?.type && layer.animation.type !== 'none' ? layer.animation.type : 'none';
}
function progressiveAnimationKey(layer: { animation?: StoryLayerAnimation } = {}) {
  const animation = layer.animation || {};
  return `${animation.type || 'none'}-${animation.durationMs || 0}-${animation.delayMs || 0}`;
}

function renderStoryListLayer(layer: StoryLayerT) {
  const items = String(layer.text || '')
    .split('\n')
    .map((item: string) => item.trim())
    .filter(Boolean);
  const variant = layer.style?.variant || 'checklist';
  if (variant === 'numbered')
    return (
      <ol className="story-list story-list-numbered">
        {items.map((item: string, index: number) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ol>
    );
  if (variant === 'bullets')
    return (
      <ul className="story-list story-list-bullets">
        {items.map((item: string, index: number) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    );
  if (variant === 'timeline')
    return (
      <div className="story-list story-list-timeline">
        {items.map((item: string, index: number) => (
          <span key={`${item}-${index}`}>
            <i>{index + 1}</i>
            {item}
          </span>
        ))}
      </div>
    );
  if (variant === 'cards')
    return (
      <div className="story-list story-list-cards">
        {items.map((item: string, index: number) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    );
  return (
    <div className="story-list story-list-checks">
      {items.map((item: string, index: number) => (
        <span key={`${item}-${index}`}>✓ {item}</span>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Completion — takeaways review
// -----------------------------------------------------------------------------

function StoryCompletion({
  config,
  onRestart,
  onClose,
}: {
  config: NormalizedStoryConfig;
  onRestart: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div className="storypro-completion">
      <div className="storypro-complete-icon">
        <CheckIcon />
      </div>
      <p className="eyebrow">{t('microStoryCards.completed')}</p>
      <h2>{config.review?.title || config.title}</h2>
      <p>{config.review?.description || t('microStoryCards.reviewFallback')}</p>
      <div className="storypro-takeaways">
        {config.cards.map((card: StoryCard, index: number) => (
          <article key={card.id}>
            <span>{index + 1}</span>
            <div>
              <strong>{card.title}</strong>
              <p>{card.description || t('microStoryCards.noExplanation')}</p>
            </div>
          </article>
        ))}
      </div>
      <div className="button-row">
        <button type="button" onClick={onRestart}>
          <RotateIcon /> {t('microStoryCards.replay')}
        </button>
        <button type="button" className="secondary-button" onClick={onClose}>
          {t('microStoryCards.closeShort')}
        </button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Style helpers (ported verbatim from the POC player)
// -----------------------------------------------------------------------------

function getCanvasStyle(config: NormalizedStoryConfig): StoryCssVars {
  const canvas = config.canvas || {};
  const ratio = String(canvas.aspectRatio || '9:16')
    .split(':')
    .map(Number);
  const aspectRatio =
    ratio.length === 2 && ratio.every(Boolean) ? `${ratio[0]} / ${ratio[1]}` : '9 / 16';
  const colors = config.theme?.colors || {};
  return {
    aspectRatio,
    width: '100%',
    maxWidth: '100%',
    '--story-canvas-w': Number(canvas.width || 1080),
    '--story-canvas-h': Number(canvas.height || 1920),
    '--story-radius': `${Number(canvas.borderRadius || 34)}px`,
    '--story-primary': colors.primary || '#0A66C2',
    '--story-secondary': colors.secondary || '#22D39A',
    '--story-accent': colors.accent || '#FFD166',
    '--story-player-text': colors.playerText || '#FFFFFF',
    '--story-player-chrome': colors.playerChrome || '#FFFFFF',
  };
}

function buildCardBackgroundStyle(card: StoryCard): CSSProperties {
  const bg = card.background || {};
  const source = getCardBackgroundSource(card);
  const style: CSSProperties = {
    backgroundColor: bg.color || '#0f172a',
    backgroundSize: bg.fit || 'cover',
    backgroundPosition: bg.position || 'center',
    backgroundRepeat: bg.repeat || 'no-repeat',
  };
  if (bg.type === 'gradient') style.backgroundImage = bg.gradient || bg.value || '';
  if (bg.type === 'image' && source) style.backgroundImage = `url(${cssUrl(source)})`;
  return style;
}

function getLayerStyle(layer: StoryLayerT): StoryCssVars {
  const style = layer.style || {};
  return {
    left: `${layer.x}%`,
    top: `${layer.y}%`,
    width: `${layer.w}%`,
    minHeight: `${layer.h}%`,
    zIndex: layer.z || 1,
    color: style.color || '#fff',
    background: style.background || 'transparent',
    textAlign: style.align || 'left',
    fontFamily: style.fontFamily || undefined,
    fontSize: `clamp(${Math.max(12, Number(style.fontSize || 30) * 0.34)}px, ${(Number(style.fontSize || 30) / 1180) * 100}cqw, ${Number(style.fontSize || 30)}px)`,
    fontWeight: Number(style.weight || 800),
    lineHeight: Number(
      style.lineHeight || (layer.type === 'text' || layer.type === 'quote' ? 1.08 : 1.12),
    ),
    letterSpacing:
      style.letterSpacing || (Number(style.fontSize || 30) > 36 ? '-0.035em' : '-0.01em'),
    padding: style.padding || undefined,
    border: getLayerBorder(style),
    borderRadius: `${style.radius ?? 24}px`,
    opacity: style.opacity ?? 1,
    '--story-layer-opacity': style.opacity ?? 1,
    textShadow: style.shadow === false ? 'none' : '0 4px 18px rgba(0,0,0,.34)',
    '--story-anim-duration': `${Number(layer.animation?.durationMs || 600)}ms`,
    '--story-anim-delay': `${Number(layer.animation?.delayMs || 0)}ms`,
    '--story-anim-easing': layer.animation?.easing || 'ease-out',
  };
}

function getLayerBorder(style: StoryLayerStyle = {}): string | undefined {
  const width =
    style.borderWidth !== undefined && style.borderWidth !== ''
      ? Number(style.borderWidth) || 0
      : Number(String(style.border || '').match(/(\d+(?:\.\d+)?)px/)?.[1] || 0);
  if (width > 0) return `${width}px solid ${style.borderColor || '#ffffff'}`;
  return style.border || undefined;
}

function sanitizeStoryHtml(html: string): string {
  const allowed = new Set([
    'b',
    'strong',
    'i',
    'em',
    'u',
    'br',
    'p',
    'ul',
    'ol',
    'li',
    'span',
    'h1',
    'h2',
    'h3',
  ]);
  return String(html || '')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/<\s*(\/?)([a-z0-9]+)([^>]*)>/gi, (_match, closing, tag) => {
      const safeTag = String(tag || '').toLowerCase();
      if (!allowed.has(safeTag)) return '';
      return `<${closing || ''}${safeTag}>`;
    });
}

function cssUrl(value: string): string {
  return String(value || '')
    .replace(/"/g, '%22')
    .replace(/\)/g, '%29');
}

// -----------------------------------------------------------------------------
// Inline icons (no external icon dep — keeps the renderer self-contained)
// -----------------------------------------------------------------------------

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function VolumeOnIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  );
}
function VolumeOffIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg
      width="38"
      height="38"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
function RotateIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="5" width="4" height="14" rx="1" />
    </svg>
  );
}
function TapIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 11V6a2 2 0 0 1 4 0v5" />
      <path d="M13 11V8a2 2 0 0 1 4 0v3" />
      <path d="M17 11V9a2 2 0 0 1 4 0v6a6 6 0 0 1-6 6h-2a6 6 0 0 1-5.2-3l-2.3-4a2 2 0 0 1 3.4-2l1.1 1.5" />
    </svg>
  );
}
